import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

// ------------------------------------------------
// AUTH SYNC (LOGIN / LOGOUT ACROSS TABS)
// ------------------------------------------------
const AUTH_SYNC_KEY = '__AUTH_SYNC__';

const authChannel = 'BroadcastChannel' in window ? new BroadcastChannel('auth-sync') : null;

// 🔒 Prevent duplicate listeners (HMR / React StrictMode)
let authSyncInitialized = false;

export function broadcastLogin() {
  if (authChannel) {
    authChannel.postMessage({ type: 'LOGIN' });
  }
  localStorage.setItem(AUTH_SYNC_KEY, `LOGIN_${Date.now()}`);
}

export function listenForAuthSync(onLogin, onLogout) {
  if (authSyncInitialized) return;
  authSyncInitialized = true;

  function handler(type) {
    if (type === 'LOGIN') onLogin();
    if (type === 'LOGOUT') onLogout();
  }

  if (authChannel) {
    authChannel.onmessage = (e) => handler(e.data?.type);
  }

  window.addEventListener('storage', (e) => {
    if (!e.key || !e.newValue) return;
    if (e.newValue.startsWith('LOGIN')) handler('LOGIN');
    if (e.newValue.startsWith('LOGOUT')) handler('LOGOUT');
  });
}

// ------------------------------------------------
// MULTI-TAB LOGOUT SYNC
// ------------------------------------------------
const LOGOUT_SYNC_KEY = '__AUTH_LOGOUT_SYNC__';

const logoutChannel = 'BroadcastChannel' in window ? new BroadcastChannel('auth-logout') : null;

export function broadcastLogout() {
  if (logoutChannel) {
    logoutChannel.postMessage('logout');
  }
  localStorage.setItem(LOGOUT_SYNC_KEY, Date.now().toString());
}
function onExternalLogout() {
  hardLogout({ fromSync: true, reload: true });
}

if (logoutChannel) {
  logoutChannel.onmessage = () => onExternalLogout();
}

window.addEventListener('storage', (e) => {
  if (e.key === LOGOUT_SYNC_KEY) {
    onExternalLogout();
  }
});
function hardLogout({ fromSync = false, message, reload = false } = {}) {
  if (isLoggedOut) return;

  isLoggedOut = true;

  try {
    localStorage.clear();
    sessionStorage.clear();

    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch {
    /* empty */
  }

  if (logoutCallback) {
    try {
      logoutCallback();
    } catch {
      /* empty */
    }
  }

  if (!fromSync) {
    broadcastLogout();
  }

  // ✅ Use backend message if available
  enqueueSnackbar(message || 'Your session has expired. Please login again.', {
    variant: 'warning',
  });

  if (navigateFn) {
    navigateFn('/auth/jwt/login', { replace: true });
  } else {
    window.location.href = '/auth/jwt/login';
  }

  if (reload) {
    window.location.reload();
  }
}

// ------------------------------------------------
// PUBLIC FRONTEND ROUTES
// ------------------------------------------------
const PUBLIC_ROUTES = [
  '/auth/jwt/login',
  '/auth/jwt/register',
  '/auth/jwt/forgot-password',
  '/auth/jwt/new-password',
  '/auth/jwt/change-password',
  '/find-workspace',
  '/vendorCandidateForm',
];

export function isPublicRoute(pathname = window.location.pathname) {
  return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
}

function shouldSkip401ForPublicExternalApis(config) {
  if (!config?.url) return false;

  const url = config.url;

  return (
    url.includes('/rest/preferences/logoPaths') ||
    url.includes('/rest/candidate/external/') ||
    url.includes('/rest/vendor-portal/candidates/external') ||
    url.includes('/rest/candDetails/external/') ||
    url.includes('/rest/candidate/otp/') ||
    url.includes('/rest/employee/external/') ||
    url.includes('/rest/employee/otp/') ||
    url.includes('/rest/emp/external/') ||
    url.includes('/rest/tenantReg/addTenant') ||
    url.includes('/rest/tenantReg/search') ||
    url.includes('/rest/tenant/status')
  );
}

// ------------------------------------------------
// EXTERNAL INJECTIONS
// ------------------------------------------------
let storeM;
let navigateFn;
let logoutCallback;
let isLoggedOut = false;

let MACHINE_IP = localStorage.getItem('MACHINE_IP') || null;

export function setMachineIP(ip) {
  MACHINE_IP = ip;
}

export const injectStoreNavigateAndLogout = ({ storeM: reduxStore, navigate, logout }) => {
  storeM = reduxStore;
  navigateFn = navigate;
  logoutCallback = logout;
};

export const resetLogoutFlag = () => {
  isLoggedOut = false;
};

// ------------------------------------------------
// AXIOS INSTANCE
// ------------------------------------------------
export const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL;

export const baseURL = process.env.REACT_APP_API_BASE_URL;

export const getApiOrigin = () => {
  const resolvedBaseUrl = process.env.REACT_APP_API_BASE_URL;
  return new URL(resolvedBaseUrl).origin;
};

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

// ------------------------------------------------
// REQUEST INTERCEPTOR
// ------------------------------------------------
axiosInstance.interceptors.request.use(
  (config) => {
    // Remove Authorization header (cookie auth only)
    if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }
    // Attach MACHINE_IP if available
    if (MACHINE_IP) {
      config.headers['X-MACHINE-IP'] = MACHINE_IP;
    }
    // Attach X-XSRF-TOKEN header from XSRF-TOKEN cookie for CSRF verification (skip read-only requests)
    const method = (config.method || 'get').toLowerCase();
    if (!['get', 'head', 'options', 'trace'].includes(method)) {
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (xsrfToken) {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }
    // Attach X-SUBSCR-SITE-ID if available, BUT skip for public/external APIs
    if (!shouldSkip401ForPublicExternalApis(config)) {
      let subscr = {};
      try {
        const stored = localStorage.getItem('selectedSubscrSite');
        if (stored && stored !== 'undefined') {
          subscr = JSON.parse(stored);
        }
      } catch (e) {
        // ignore parse error
      }
      const activeSiteId = subscr?.subscrSiteId;
      if (activeSiteId) {
        config.headers['X-SUBSCR-SITE-ID'] = activeSiteId;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ------------------------------------------------
// RESPONSE INTERCEPTOR
// ------------------------------------------------
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error = null) => {
  failedQueue.forEach(({ resolve, reject, originalRequest }) => {
    // eslint-disable-next-line no-unused-expressions
    error ? reject(error) : resolve(axiosInstance(originalRequest));
  });
  failedQueue = [];
};

let isRefreshingPermissions = false;
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (!originalRequest || isLoggedOut) {
      return Promise.reject(error);
    }

    // ------------------------------------------------
    // ✅ HARD SKIP 1: Public frontend routes
    // ------------------------------------------------
    if (status === 401 && isPublicRoute()) {
      return Promise.reject(error);
    }

    // ------------------------------------------------
    // ✅ HARD SKIP 2: candidateForm + logoPaths
    // ------------------------------------------------

    if (status === 401 && shouldSkip401ForPublicExternalApis(originalRequest)) {
      return Promise.reject(error);
    }
    // ------------------------------------------------
    // ✅ PLAN LIMIT EXCEEDED (403 with specific errorCode)
    // ------------------------------------------------
    if (status === 403 && error?.response?.data?.errorCode === 'PLAN_LIMIT_EXCEEDED') {
      const planData = error.response.data;
      enqueueSnackbar(
        planData.message || 'This feature is not available in your current plan. Please upgrade.',
        { variant: 'warning', autoHideDuration: 5000 }
      );
      error._handled = true; // Mark as handled
      return Promise.reject(error);
    }

    // ------------------------------------------------
    // ✅ ENTITY FROZEN (403 with ENTITY_FROZEN errorCode)
    // ------------------------------------------------
    if (status === 403 && error?.response?.data?.errorCode === 'ENTITY_FROZEN') {
      const frozenData = error.response.data;
      enqueueSnackbar(
        frozenData.message || 'This data is frozen due to plan limits. Please upgrade to access it.',
        { variant: 'warning', autoHideDuration: 6000 }
      );
      error._handled = true;
      return Promise.reject(error);
    }

    // ------------------------------------------------
    // ✅ MALFORMED REQUEST / SECURITY REJECTION (400)
    // ------------------------------------------------
    const responseData = error?.response?.data;
    const isMalformedOrSecurity =
      status === 400 &&
      (responseData?.errorCode === 'MALFORMED_REQUEST' ||
        /sql injection|xss/i.test(responseData?.message || '') ||
        /sql injection|xss/i.test(responseData?.error || ''));

    if (isMalformedOrSecurity) {
      const genericMessage = 'Malicious input or script detected. Please verify your entries and try again.';
      enqueueSnackbar(genericMessage, {
        variant: 'error',
        autoHideDuration: 6000,
      });
      error._handled = true;
      return Promise.reject(error);
    }

    // ------------------------------------------------
    // SERVER ERRORS LOGGING (500+)
    // ------------------------------------------------
    if (status >= 500) {
      console.error('[API Server Error]', {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status,
        error: error?.response?.data,
      });
    }

    // Refresh permissions on 403 (Forbidden) — permission revoked mid-session
    if (
      status === 403 &&
      !originalRequest._permissionRetry &&
      !originalRequest.url?.includes('/rest/roleConfig/currentPermissions')
    ) {
      originalRequest._permissionRetry = true;

      if (!isRefreshingPermissions && storeM) {
        isRefreshingPermissions = true;

        try {
          const permissionRes = await axiosInstance.get('/rest/roleConfig/currentPermissions');
          storeM.dispatch({
            type: 'permission/permissionsSyncedFrom403',
            payload: permissionRes?.data || {},
          });
        } catch (permissionError) {
          console.error('[403 Handler] Failed to fetch permissions:', permissionError);
          storeM.dispatch({
            type: 'permission/permissionsSyncFailedFrom403',
            payload:
              permissionError?.response?.data ||
              permissionError?.message ||
              'Failed to sync permissions',
          });
        } finally {
          isRefreshingPermissions = false;
        }
      } else {
        console.warn(
          '[403 Handler] SKIPPED permission sync — storeM:',
          !!storeM,
          'isRefreshingPermissions:',
          isRefreshingPermissions
        );
      }
    }

    // ------------------------------------------------
    // AUTH ENDPOINTS (never refresh / logout)
    // ------------------------------------------------
    const authEndpoints = [
      '/auth/login',
      '/auth/logout',
      '/auth/refresh',
      '/auth/register',
      '/auth/csrf-token',
    ];

    const isAuthEndpoint = authEndpoints.some((ep) => originalRequest.url?.includes(ep));

    // ------------------------------------------------
    // PROTECTED FLOW — handle 401 (token expired)
    // ------------------------------------------------
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });
      }

      isRefreshing = true;

      try {
        // Attempt refresh
        await axiosInstance.post('/auth/refresh');
        processQueue();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        // Extract backend error message
        const apiMessage =
          refreshError?.response?.data?.message ||
          refreshError?.response?.data?.error ||
          'Your session has expired. Please login again.';

        try {
          await axiosInstance.post('/auth/logout');
        } catch {
          /* empty */
        }

        // ✅ Pass message here
        hardLogout({ message: apiMessage });

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

